import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, RouteReuseStrategy, withHashLocation } from '@angular/router';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { DragulaModule } from 'ng2-dragula';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { NgxStripeModule } from 'ngx-stripe';
import { ToastrModule } from 'ngx-toastr';

import { provideAppStore } from '@hosty-app/app-store';
import { JwtInterceptor } from '@hosty-app/core';
import { OnesignalService } from '@hosty-app/services';

import { environment } from '../environments/environment';

import { ApiPrefixInterceptor } from './core/interceptors/api-prefix.interceptor';
import { BlockUserInterceptor } from './core/interceptors/block-user.interceptor';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { RefreshTokenInterceptor } from './core/interceptors/refresh-token.interceptor';
import { RouteReusableStrategy } from './core/route-reusable-strategy';
import { routes } from './routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    provideRouter(routes, ...(environment.isMobile ? [withHashLocation()] : [])),
    provideAppStore(),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production, // Restrict extension to log-only mode
    }),
    importProvidersFrom(
      DragulaModule.forRoot(),
      NgxStripeModule.forRoot(environment.stripeApiKey),
      ToastrModule.forRoot({
        positionClass: 'toast-bottom-center',
        iconClasses: {
          error: 'hosty-toast hosty-toast-error',
          info: 'hosty-toast hosty-toast-info',
          success: 'hosty-toast hosty-toast-success',
          warning: 'hosty-toast hosty-toast-warning',
        },
      }),
    ),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const oneSignal = inject(OnesignalService);
        return () => {
          return oneSignal
            .init({
              appId: environment.oneSignalAppId,
              autoRegister: true,
            })
            .catch((err) => {
              console.log(err);
              return;
            });
        };
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiPrefixInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BlockUserInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy,
    },
    provideEnvironmentNgxMask(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
};
